/* Input park: the cell editor converts with the Table round (component-map.md). */
import { Box, FormControl, FormLabel, Input } from '@chakra-ui/react';
import { Nullable } from '@main/shared/types';
import { FilterMode, isIsEmptyOrIsNotEmpty } from '@main/shared/url-helpers';
import { isEmpty } from '@main/shared/utils/is-empty';
import { CellContext, ColumnDef, Row, SortDirection } from '@tanstack/react-table';
import equal from 'fast-deep-equal';
import { ElementRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod/v4';

import { EditablePopover } from '../../editable';
import { FloatingActionButtons } from '../../floating-action-buttons/floating-action-buttons';
import { OverflowContainer } from '../../overflow-tooltip';
import { errorToast } from '../../toast';
import { BulkChangeConfigBase, useSyncIsBulkLoading } from '../bulk-actions';
import { createFilterHelper } from '../filters';
import { FilterLabelRow } from '../filters/filter-label-row';
import { FilterModeSelector } from '../filters/filter-mode';
import { CellContainer } from '../shared/cell';
import { RenderLink } from '../shared/render-link';
import { CanEditGuard, getEditable, ifBulkEditable } from '../table';
import {
  bulkScalarClearAction,
  BulkScalarEditAction,
  ScalarBulkClearButton,
} from './scalar-bulk-clear';

type EditProps<TData> = {
  onChange: (row: Row<TData>, value: string) => void;
  canEditGuard?: CanEditGuard<TData, string | null | undefined>;
};

export type BulkLinkOnChange<TData> = (
  rows: Row<TData>[],
  value: string | undefined,
  action?: BulkScalarEditAction,
) => unknown;
export type UseBulkLinkChangeHook<TData> = () => readonly [BulkLinkOnChange<TData>, boolean];

type LinkColumnConfig<TData, TFilter, TSort> = {
  id: string;
  header: string;
  edit?: EditProps<TData>;
  bulkEdit?: {
    useBulkChange: UseBulkLinkChangeHook<TData>;
    allowClearValue?: boolean;
    bulkInitialValue?: string;
  } & Omit<BulkChangeConfigBase, 'onClick' | 'wrapper'>;
  getGlobalFilterCondition?(filterValue: string): Nullable<TFilter>;
  getColumnFilterCondition?(filterMode: FilterMode, filterValue: string): Nullable<TFilter>;
  getColumnSort?(sortDirection: SortDirection): TSort;
};

const filterHelper = createFilterHelper({
  filterSchema: z.string(),
  filterFn,
});

function filterFn(
  value: string | undefined | null,
  filterValue: string | null,
  filterMode?: FilterMode,
): boolean {
  if (isIsEmptyOrIsNotEmpty(filterMode)) {
    if (filterMode === FilterMode.IsEmpty) {
      return isEmpty(value);
    }
    if (filterMode === FilterMode.IsNotEmpty) {
      return !isEmpty(value);
    }
  }
  if (!filterValue) {
    return true;
  }

  const isContainingValue = !!value?.toLowerCase().includes(filterValue.toLowerCase());

  if (filterMode === FilterMode.Excludes) {
    return !isContainingValue;
  }

  return isContainingValue;
}

const anyValueChanged = <T,>(
  rows: Row<T>[],
  columnId: string,
  newValue: string | undefined,
): boolean =>
  rows.some((row) => {
    const value = row.getValue(columnId);
    const bothFalsy = !value && !newValue;
    return !bothFalsy && value !== newValue;
  });

const getBulkInitialValue = <T,>(rows: Row<T>[], columnId: string): string | undefined => {
  const [firstRow, ...restRows] = rows;
  if (!firstRow) return undefined;

  const value = firstRow.getValue<string>(columnId);
  return restRows.every((row) => equal(row.getValue(columnId), value)) ? value : undefined;
};

export function link<TData, TFilter, TSort>() {
  return ({
    edit,
    bulkEdit,
    ...columnDef
  }: ColumnDef<TData, string | null | undefined> &
    LinkColumnConfig<TData, TFilter, TSort>): ColumnDef<TData, string | null | undefined> => {
    const editable = getEditable(edit);
    const useBulkChange = bulkEdit?.useBulkChange;

    return {
      enableGlobalFilter: false,
      enableColumnFilter: false,
      enableSorting: false,
      sortDescFirst: false,

      ...columnDef,

      cell: (context) => {
        const value = context.getValue();

        if (edit && editable(context.row.original, context)) {
          return <EditableCell edit={edit} context={context} />;
        }

        return (
          <CellContainer cell={context.cell} p={0}>
            <OverflowContainer>
              <RenderLink value={value ?? undefined} />
              <FloatingActionButtons
                isAttached={false}
                buttons={columnDef.meta?.cell?.actions?.(context.row) ?? []}
              />
            </OverflowContainer>
          </CellContainer>
        );
      },

      filterFn: filterHelper.filterFn,

      meta: {
        ...columnDef.meta,
        columnType: 'link',
        editable,
        name: columnDef.header,
        globalFilterFn: filterFn,
        bulkEdit: ifBulkEditable(edit, bulkEdit, () => {
          if (!bulkEdit || !useBulkChange) {
            throw new Error('Bulk edit config requires a bulk change handler');
          }

          return {
            ...bulkEdit,
            id: columnDef.id ?? '',
            editor: (editorRows, trigger, isInMenu, onOpenChange, labelledBy) => (
              <BulkLinkEditor
                id={columnDef.id ?? ''}
                key={columnDef.id}
                trigger={trigger}
                rows={editorRows}
                useBulkChange={useBulkChange}
                allowClearValue={bulkEdit.allowClearValue}
                defaultValue={
                  bulkEdit.bulkInitialValue ?? getBulkInitialValue(editorRows, columnDef.id ?? '')
                }
                isInMenu={isInMenu}
                onOpenChange={onOpenChange}
                labelledBy={labelledBy}
              />
            ),
          };
        }),
        // Tanstack table filters are untyped. Type safety is enforced via filter/column helpers
        getGlobalFilterCondition: columnDef.getGlobalFilterCondition as never,
        getColumnFilterCondition: columnDef.getColumnFilterCondition as never,
        getColumnSort: columnDef.getColumnSort as never,

        filter: {
          filterSchema: filterHelper.filterSchema,

          DisplayFilter: ({ column }) => {
            const { t } = useTranslation('ui');

            const mode = filterHelper.getFilterMode(column);
            return (
              <span>
                {isIsEmptyOrIsNotEmpty(mode) ? (
                  mode === FilterMode.IsEmpty ? (
                    t('table.filter.isEmpty')
                  ) : (
                    t('table.filter.isNotEmpty')
                  )
                ) : (
                  <>
                    {mode === FilterMode.Excludes && t('table.filter.notLabel')}
                    {filterHelper.getFilterValue(column)}
                  </>
                )}
              </span>
            );
          },

          EditFilter: ({ column }) => {
            const { t } = useTranslation('ui');

            const mode = filterHelper.getFilterMode(column);
            const isExcluding = mode === FilterMode.Excludes;
            const value = filterHelper.getFilterValue(column);
            const hasInput = !isIsEmptyOrIsNotEmpty(mode);
            return (
              <FormControl p={3}>
                <FormLabel fontWeight="medium" mb={hasInput ? undefined : 0}>
                  <FilterLabelRow label={columnDef.header}>
                    <FilterModeSelector {...filterHelper} column={column} />
                  </FilterLabelRow>
                </FormLabel>
                {hasInput && (
                  <Input
                    autoFocus
                    size="sm"
                    placeholder={t('table.column.text.filterPlacehoder')}
                    borderRadius="base"
                    value={value || ''}
                    aria-label={`${columnDef.header} ${
                      isExcluding ? t('table.filter.excludes') : t('table.filter.includes')
                    }`}
                    onChange={(event) =>
                      filterHelper.setFilterValue(column, event.target.value || null)
                    }
                  />
                )}
              </FormControl>
            );
          },
        },
      },
    };
  };
}

type EditableCellProps<TData> = {
  edit: EditProps<TData>;
  context: CellContext<TData, string | null | undefined>;
};

function EditableCell<TData>({ edit, context }: EditableCellProps<TData>) {
  const { t } = useTranslation('ui');
  const cellValue = context.getValue();
  const inputRef = useRef<ElementRef<'input'>>(null);
  const columnDef = context.column.columnDef;

  const onCellUpdate = () => {
    const value = inputRef.current?.value;
    const isValid = inputRef.current?.validity.valid;

    if (!isValid) {
      errorToast(t('table.invalidValue'));
      return;
    }

    if (value !== undefined && value !== cellValue) {
      edit.onChange(context.row, value);
    }
  };

  return (
    <EditablePopover onClose={onCellUpdate}>
      <EditablePopover.Reference>
        <EditablePopover.Trigger>
          <CellContainer cell={context.cell} p={0}>
            <OverflowContainer>
              <RenderLink value={cellValue ?? undefined} />
              <FloatingActionButtons
                isAttached={false}
                buttons={columnDef.meta?.cell?.actions?.(context.row) ?? []}
              />
            </OverflowContainer>
          </CellContainer>
        </EditablePopover.Trigger>
      </EditablePopover.Reference>
      <EditablePopover.Content bgColor="gray.25" _dark={{ bgColor: 'gray.700' }}>
        <Input
          type="url"
          ref={inputRef}
          autoFocus
          size="sm"
          variant="unstyled"
          p={4}
          maxH="50px"
          outline="2px solid"
          outlineColor="chakra-border-color"
          defaultValue={cellValue ?? ''}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur();
            }
          }}
        />
      </EditablePopover.Content>
    </EditablePopover>
  );
}
type BulkLinkEditorProps<TData> = {
  id: string;
  defaultValue?: string;
  trigger: JSX.Element;
  rows: Row<TData>[];
  useBulkChange: UseBulkLinkChangeHook<TData>;
  allowClearValue?: boolean;
  isInMenu?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  labelledBy?: string;
};

function BulkLinkEditor<TData>({
  id,
  defaultValue,
  trigger,
  rows,
  useBulkChange,
  allowClearValue = false,
  isInMenu,
  onOpenChange,
  labelledBy,
}: BulkLinkEditorProps<TData>) {
  const normalizedDefaultValue = defaultValue ?? '';
  const { t } = useTranslation('ui');
  const inputRef = useRef<ElementRef<'input'>>(null);
  const skipNextUpdateRef = useRef(false);
  const [onChange, isLoading] = useBulkChange();
  useSyncIsBulkLoading(id, isLoading);

  const onUpdate = async () => {
    if (isLoading) {
      return;
    }

    if (skipNextUpdateRef.current) {
      skipNextUpdateRef.current = false;
      return;
    }

    const nextValue = inputRef.current?.value.trim() ?? '';

    if (nextValue === '') {
      if (!allowClearValue) {
        errorToast(t('table.invalidValue'));
      }
      return;
    }

    if (nextValue === normalizedDefaultValue) {
      return;
    }

    if (!(inputRef.current?.validity.valid ?? true)) {
      errorToast(t('table.invalidValue'));
      return;
    }

    if (!anyValueChanged(rows, id, nextValue)) {
      return;
    }

    await onChange(rows, nextValue);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      skipNextUpdateRef.current = false;
    }
    onOpenChange?.(isOpen);
  };

  const handleClear = async (setOpen: (isOpen: boolean) => void) => {
    if (isLoading) {
      return;
    }

    const previousValue = inputRef.current?.value;
    skipNextUpdateRef.current = true;
    if (inputRef.current) {
      inputRef.current.value = '';
    }

    const didUpdate = await onChange(
      rows,
      previousValue ?? normalizedDefaultValue,
      bulkScalarClearAction,
    );
    if (didUpdate !== false) {
      skipNextUpdateRef.current = true;
      setOpen(false);
      return;
    }

    skipNextUpdateRef.current = false;
    if (inputRef.current && previousValue !== undefined) {
      inputRef.current.value = previousValue;
    }
  };

  return (
    <EditablePopover
      onClose={onUpdate}
      onOpenChange={handleOpenChange}
      closeOnBlur={false}
      openEvent={isInMenu ? 'mousedown' : undefined}
      placement={isInMenu ? 'left-start' : 'top-end'}
    >
      <EditablePopover.Trigger>{trigger}</EditablePopover.Trigger>
      <EditablePopover.Content
        bgColor="gray.25"
        _dark={{ bgColor: 'gray.700' }}
        offset={-1}
        data-testid={`bulk-editor-${id}`}
      >
        {(context) => {
          const input = (
            <Input
              type="url"
              ref={inputRef}
              autoFocus
              size="sm"
              variant="unstyled"
              py={4}
              pl={4}
              pr={allowClearValue ? 10 : 4}
              maxH="50px"
              outline="2px solid"
              outlineColor="chakra-border-color"
              defaultValue={normalizedDefaultValue}
              onBlur={() => context.setOpen(false)}
              placeholder={t('table.column.text.filterPlacehoder')}
              aria-labelledby={labelledBy}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  event.stopPropagation();
                  const nextValue = event.currentTarget.value.trim();

                  if (nextValue !== '' && !event.currentTarget.validity.valid) {
                    errorToast(t('table.invalidValue'));
                    return;
                  }

                  context.setOpen(false);
                }
              }}
            />
          );

          return allowClearValue ? (
            <Box position="relative">
              {input}
              <ScalarBulkClearButton
                isDisabled={isLoading}
                onClick={() => void handleClear(context.setOpen)}
              />
            </Box>
          ) : (
            input
          );
        }}
      </EditablePopover.Content>
    </EditablePopover>
  );
}
